/**
 * =============================================================================
 * Hook: useReferralReferralActions
 * Versão: 1.0.0
 *
 * Descrição:
 *   Encapsula ações administrativas sobre indicações:
 *   - reprocessar (chamar `reprocess_affiliate_referral` para tentar criar
 *     contato/negócio novamente caso a primeira tentativa tenha falhado).
 *
 *   Outras ações (qualificar, converter, perder manualmente) serão adicionadas
 *   nas próximas etapas conforme as RPCs forem criadas.
 *
 * RELEASE:
 *   2026-04-26 v1.0.0 - Criação inicial.
 * =============================================================================
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useReferralReferralActions');

export function useReferralReferralActions() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const reprocess = useMutation({
    mutationFn: async (referralId: string) => {
      const { data, error } = await (supabase.rpc as any)(
        'reprocess_affiliate_referral',
        {
          p_referral_id: referralId,
          p_tenant_hint: currentTenant?.id || null,
        },
      );
      if (error) {
        logger.error('Falha ao reprocessar indicação', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      toast.success('Indicação reprocessada com sucesso');
      queryClient.invalidateQueries({
        queryKey: ['referral-referrals-admin', currentTenant?.id],
      });
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Erro ao reprocessar indicação');
    },
  });

  return {
    reprocess: reprocess.mutate,
    reprocessAsync: reprocess.mutateAsync,
    isReprocessing: reprocess.isPending,
  };
}
